import { Loader2, MessageCircle, Paperclip, Send, Smile, UserPlus } from 'lucide-react';
import { useAuth } from '@/app/providers';
import { Avatar, Button } from '@/components/ui';
import { toast } from '@/components/feedback/toast';
import { useAuthModal } from '@/features/auth/authModalStore';
import { ChatPane } from '@/features/communities/community-view/ChatPane';
import type { Channel } from '@/features/communities/community-view/channels';
import { displayName } from '@/features/communities/community-view/cvHelpers';
import { useAddFriend, useFriendship, useSentFriendRequest } from '@/features/profile/hooks/useSocial';
import { cn } from '@/lib/utils';
import type { Profile } from '@/types';
import * as api from '../api/directMessages';
import {
  useDeleteDirectMessage,
  useDirectMembers,
  useDirectMessages,
  useDirectReactions,
  useEditDirectMessage,
  useSendDirectMessage,
  useToggleDirectReaction,
} from '../hooks/useDirectMessages';
import { useDirectRealtime } from '../hooks/useDirectRealtime';
import { usePinActions, usePinnedIds } from '../hooks/usePins';

const DM_CHANNEL: Channel = { id: 'dm', name: 'direct-message', kind: 'text', section: 'DM' };
const THREAD_SHELL =
  'flex h-full flex-col overflow-hidden bg-black/25 shadow-[0_18px_60px_rgba(0,0,0,0.18)] backdrop-blur-xl';

export function DirectMessageThread({
  conversationId,
  otherProfile,
  compact = false,
}: {
  conversationId: string | undefined;
  otherProfile: Profile;
  compact?: boolean;
}) {
  const { user, profile } = useAuth();
  const openAuth = useAuthModal((s) => s.openAuth);
  const { data: friendship } = useFriendship(user?.id, otherProfile.id);
  const { data: requestSent } = useSentFriendRequest(user?.id, otherProfile.id);
  const addFriend = useAddFriend(user?.id, otherProfile.id);
  const { data: members = [] } = useDirectMembers(conversationId);
  const { data: messages = [], isLoading } = useDirectMessages(conversationId);
  const { data: reactions = [] } = useDirectReactions(
    conversationId,
    messages.map((m) => m.id),
  );
  const sendMessage = useSendDirectMessage(conversationId);
  const editMessage = useEditDirectMessage(conversationId);
  const deleteMessage = useDeleteDirectMessage(conversationId);
  const toggleReaction = useToggleDirectReaction(conversationId);
  const { pin, unpin } = usePinActions();
  const { data: pinnedIds = [] } = usePinnedIds(messages.map((m) => m.id));
  const pinnedSet = new Set(pinnedIds);
  const selfName = profile?.display_name ?? user?.email ?? null;
  const { typing, sendTyping } = useDirectRealtime(conversationId, selfName);

  const canChat = Boolean(user && conversationId);

  if (!user) {
    return (
      <div className="flex h-full min-h-[320px] flex-col items-center justify-center gap-4 rounded-lg border border-card-line bg-card/60 p-8 text-center">
        <div className="grid h-12 w-12 place-items-center rounded-full bg-accent-soft text-accent">
          <MessageCircle size={21} />
        </div>
        <div>
          <div className="text-base font-extrabold text-hi">Log in to message {displayName(otherProfile)}</div>
          <p className="mt-1 text-sm text-mid">DMs are live and private between members.</p>
        </div>
        <Button onClick={openAuth}>Log in</Button>
      </div>
    );
  }

  // DMs are gated behind friendship: you can only message friends. Until then,
  // show an Add button — or a "Request sent" state once a request is pending.
  const isSelf = user.id === otherProfile.id;
  if (!friendship && !isSelf) {
    return (
      <FriendGate
        otherProfile={otherProfile}
        requestSent={Boolean(requestSent)}
        pending={addFriend.isPending}
        compact={compact}
        onAdd={() =>
          addFriend.mutate(undefined, {
            onSuccess: () => toast('Friend request sent.'),
            onError: (err) => toast((err as Error).message || 'Could not send friend request', 'error'),
          })
        }
      />
    );
  }

  if (!conversationId || isLoading) {
    return (
      <ChatStartingShell name={displayName(otherProfile)} compact={compact} />
    );
  }

  return (
    <div className={cn(THREAD_SHELL, compact ? 'min-h-[520px]' : 'min-h-0')}>
      <ChatPane
        channel={{ ...DM_CHANNEL, name: displayName(otherProfile) }}
        messages={messages}
        members={members}
        reactions={reactions}
        roleOf={() => 'MEMBER'}
        currentUserId={user.id}
        isOwner={false}
        signedIn={Boolean(user)}
        joined={canChat}
        sending={sendMessage.isPending}
        typing={typing}
        onSend={(body, opts) => {
          sendMessage.mutate({
            conversationId,
            senderId: user.id,
            body,
            media: opts?.media ?? null,
            replyToId: opts?.replyToId ?? null,
          });
        }}
        onUploadImage={(file) => api.uploadMessageImage(file, conversationId)}
        onToggleReaction={(messageId, emoji) => toggleReaction.mutate({ messageId, userId: user.id, emoji })}
        onTyping={sendTyping}
        onEdit={(id, body) => editMessage.mutate({ id, body })}
        onDelete={(id) => deleteMessage.mutate(id)}
        onRequireJoin={openAuth}
        onOpenProfile={() => undefined}
        onReport={() => undefined}
        onTogglePin={(m, pinned) => {
          if (pinned) {
            unpin.mutate(m.id, { onSuccess: () => toast('Removed from pinned.') });
          } else {
            pin.mutate(m.id, {
              onSuccess: () => toast('Pinned to your inbox.'),
              onError: (err) => toast((err as Error).message || 'Could not pin message', 'error'),
            });
          }
        }}
        pinnedIds={pinnedSet}
        hideHeader
      />
    </div>
  );
}

function FriendGate({
  otherProfile,
  requestSent,
  pending,
  compact,
  onAdd,
}: {
  otherProfile: Profile;
  requestSent: boolean;
  pending: boolean;
  compact: boolean;
  onAdd: () => void;
}) {
  const name = displayName(otherProfile);
  return (
    <div className={cn(THREAD_SHELL, compact ? 'min-h-[520px]' : 'min-h-0')}>
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
        <Avatar src={otherProfile.avatar_url} name={name} size={76} />
        <div>
          <div className="text-base font-extrabold text-hi">
            {requestSent ? 'Request sent' : `Add ${name} to message them`}
          </div>
          <p className="mx-auto mt-1 max-w-xs text-sm leading-relaxed text-mid">
            {requestSent
              ? `Waiting for ${name} to accept your friend request — you'll be able to chat once they do.`
              : `You can only message people you're friends with. Send a request to start the conversation.`}
          </p>
        </div>
        {!requestSent && (
          <Button onClick={onAdd} disabled={pending}>
            <UserPlus size={15} />
            {pending ? 'Sending…' : `Add ${name}`}
          </Button>
        )}
      </div>
    </div>
  );
}

function ChatStartingShell({ name, compact }: { name: string; compact: boolean }) {
  return (
    <div className={cn(THREAD_SHELL, compact ? 'min-h-[520px]' : 'min-h-0')}>
      <div className="flex min-h-0 flex-1 items-center justify-center px-6 text-center">
        <div>
          <Loader2 size={26} className="mx-auto mb-3 animate-spin text-accent" />
          <div className="text-sm font-semibold text-white/[0.72]">Starting direct message...</div>
          <div className="mt-1 text-xs text-white/[0.46]">The chat will open here with the composer ready.</div>
        </div>
      </div>
      <div className="relative mx-[22px] mb-[18px] mt-1 flex-none rounded-md border border-white/10 bg-white/[0.03] opacity-70 backdrop-blur-[8px]">
        <div className="flex items-end gap-1.5 py-2 pl-2.5 pr-2">
          <div className="grid h-9 w-9 flex-none place-items-center rounded-[10px] text-white/[0.46]">
            <Paperclip size={18} />
          </div>
          <div className="grid h-9 flex-none place-items-center rounded-[10px] px-2 text-[12px] font-extrabold tracking-[0.5px] text-white/[0.46]">
            GIF
          </div>
          <div className="flex-1 px-1 py-2 text-[14.5px] text-white/[0.46]">Message {name}</div>
          <div className="grid h-9 w-9 flex-none place-items-center rounded-[10px] text-white/[0.46]">
            <Smile size={18} />
          </div>
          <div className="grid h-9 w-9 flex-none place-items-center rounded-[10px] bg-white/[0.08] text-white/[0.46]">
            <Send size={16} />
          </div>
        </div>
      </div>
    </div>
  );
}
